import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import "leaflet/dist/leaflet.css";

import type { Property } from "@shared/schema";

let L: any;
let loadLeafletPromise: Promise<void> | null = null;

async function ensureLeafletLoaded(): Promise<void> {
  if (typeof window === "undefined") return;
  if (L) return;
  if (!loadLeafletPromise) {
    loadLeafletPromise = (async () => {
      const leaflet = await import("leaflet");
      L = leaflet.default || leaflet;
      (window as any).L = L;

      delete (L.Icon.Default.prototype as any)._getIconUrl;
      L.Icon.Default.mergeOptions({
        iconRetinaUrl:
          "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
        iconUrl:
          "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
        shadowUrl:
          "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
      });
    })();
  }
  await loadLeafletPromise;
}

export type MapBounds = {
  north: number;
  south: number;
  east: number;
  west: number;
};

export type MapViewHandle = {
  fitToBounds: (bounds: MapBounds | null) => void;
  highlightById: (id: string) => void;
};

export type MapViewProps = {
  properties: Property[];
  selectedPropertyId?: string | null;
  onPropertySelect?: (property: Property) => void;
  onMarkerClick?: (id: string) => void;
  onBoundsChange?: (bounds: MapBounds) => void;
  targetBounds?: MapBounds | null;
  className?: string;
  height?: string;
  modalOpen?: boolean;
};

const MapView = forwardRef<MapViewHandle, MapViewProps>(
  (
    {
      properties,
      selectedPropertyId = null,
      onPropertySelect,
      onMarkerClick,
      onBoundsChange,
      targetBounds = null,
      className = "",
      height = "100%",
      modalOpen = false,
    },
    ref,
  ) => {
    const mapContainerRef = useRef<HTMLDivElement | null>(null);
    const mapRef = useRef<any>(null);
    const markersRef = useRef<Map<string, any>>(new Map());
    const didFitBoundsRef = useRef<boolean>(false);
    const interactionStateRef = useRef<{ dragging: boolean; touchZoom: boolean; scrollWheelZoom: boolean; doubleClickZoom: boolean; boxZoom: boolean; keyboard: boolean; tap?: boolean } | null>(null);

    const validProperties = useMemo(() => {
      return properties.filter((property) => {
        const lat =
          typeof property.latitude === "string"
            ? parseFloat(property.latitude)
            : property.latitude;
        const lng =
          typeof property.longitude === "string"
            ? parseFloat(property.longitude)
            : property.longitude;
        return lat && lng && !Number.isNaN(lat) && !Number.isNaN(lng);
      });
    }, [properties]);

    useImperativeHandle(
      ref,
      () => ({
        fitToBounds: (bounds) => {
          if (!bounds || !mapRef.current || !L) return;
          try {
            const latLngBounds = L.latLngBounds(
              [bounds.south, bounds.west],
              [bounds.north, bounds.east],
            );
            mapRef.current.fitBounds(latLngBounds.pad(0.08));
          } catch (error) {
            console.warn("MapView.fitToBounds failed", error);
          }
        },
        highlightById: (id: string) => {
          const marker = markersRef.current.get(id);
          if (!marker) return;
          try {
            if (marker.openPopup) {
              marker.openPopup();
            }
            if (marker._icon) {
              marker._icon.classList.add("marker-flash");
              setTimeout(() => {
                marker._icon?.classList.remove("marker-flash");
              }, 900);
            }
          } catch (error) {
            console.warn("MapView.highlightById failed", error);
          }
        },
      }),
      [],
    );

    useEffect(() => {
      let cancelled = false;

      async function initMap() {
        await ensureLeafletLoaded();
        if (cancelled || !mapContainerRef.current || !L) return;

        const map = L.map(mapContainerRef.current).setView([25.79, -80.13], 11);
        mapRef.current = map;

        const baseLayer = L.tileLayer(
          "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
          {
            attribution:
              "&copy; <a href=\"https://www.openstreetmap.org/copyright\">OpenStreetMap</a>",
            maxZoom: 19,
            className: "map-tiles-automatic",
          },
        );
        baseLayer.addTo(map);

        const handleMoveEnd = () => {
          if (!onBoundsChange || !mapRef.current) return;
          const bounds = map.getBounds();
          onBoundsChange({
            north: bounds.getNorth(),
            south: bounds.getSouth(),
            east: bounds.getEast(),
            west: bounds.getWest(),
          });
        };

        map.on("moveend", handleMoveEnd);
        map.on("zoomend", handleMoveEnd);
        map.on("dragend", handleMoveEnd);

        setTimeout(handleMoveEnd, 1000);
      }

      initMap();

      return () => {
        cancelled = true;
        if (mapRef.current) {
          mapRef.current.remove();
          mapRef.current = null;
        }
      };
    }, [onBoundsChange]);

    useEffect(() => {
      async function syncMarkers() {
        await ensureLeafletLoaded();
        if (!mapRef.current || !L) return;

        const map = mapRef.current;

        markersRef.current.forEach((marker) => map.removeLayer(marker));
        markersRef.current.clear();

        const leafletMarkers: any[] = [];

        validProperties.forEach((property) => {
          const lat =
            typeof property.latitude === "string"
              ? parseFloat(property.latitude)
              : property.latitude;
          const lng =
            typeof property.longitude === "string"
              ? parseFloat(property.longitude)
              : property.longitude;
          if (!lat || !lng) return;

          const marker = L.marker([lat, lng], {
            icon: L.divIcon({
              html: `
                <div class="mobile-marker" style="
                  background: #ff385c;
                  width: 14px;
                  height: 14px;
                  border-radius: 50%;
                  border: 2px solid #fff;
                  box-shadow: 0 6px 18px rgba(0,0,0,0.28);
                "></div>
              `,
              className: "mobile-map-marker",
              iconSize: [18, 18],
              iconAnchor: [9, 9],
            }),
          });

          marker.on("click", () => {
            if (onPropertySelect) onPropertySelect(property);
            if (onMarkerClick) onMarkerClick(property.id);
          });

          marker.addTo(map);
          markersRef.current.set(property.id, marker);
          leafletMarkers.push(marker);
        });

        if (leafletMarkers.length > 0 && !didFitBoundsRef.current) {
          const group = new L.FeatureGroup(leafletMarkers);
          map.fitBounds(group.getBounds().pad(0.08));
          didFitBoundsRef.current = true;
        }
      }

      syncMarkers().catch((error) =>
        console.warn("MapView.syncMarkers failed", error),
      );
    }, [validProperties, onPropertySelect, onMarkerClick]);

    useEffect(() => {
      if (!selectedPropertyId || !markersRef.current.size || !L) return;

      markersRef.current.forEach((marker, id) => {
        marker.setIcon(
          L.divIcon({
            html:
              selectedPropertyId === id
                ? `
                  <div class="selected-marker" style="
                    background: #2563eb;
                    color: #fff;
                    padding: 6px 14px;
                    border-radius: 22px;
                    border: 2px solid #fff;
                    font-weight: 600;
                    font-size: 12px;
                    box-shadow: 0 16px 28px rgba(37,99,235,0.35);
                    position: relative;
                    white-space: nowrap;
                  ">
                    $${Number(
                      properties.find((p) => p.id === id)?.price ?? 0,
                    ).toLocaleString()}
                    <div style="
                      position: absolute;
                      left: 50%;
                      bottom: -8px;
                      transform: translateX(-50%);
                      width: 0;
                      height: 0;
                      border-left: 8px solid transparent;
                      border-right: 8px solid transparent;
                      border-top: 8px solid #2563eb;
                    "></div>
                  </div>
                `
                : `
                  <div class="mobile-marker" style="
                    background: #ff385c;
                    width: 14px;
                    height: 14px;
                    border-radius: 50%;
                    border: 2px solid #fff;
                    box-shadow: 0 6px 18px rgba(0,0,0,0.28);
                  "></div>
                `,
            className: selectedPropertyId === id
              ? "mobile-map-marker selected"
              : "mobile-map-marker",
            iconSize: selectedPropertyId === id ? [94, 38] : [18, 18],
            iconAnchor: selectedPropertyId === id ? [47, 36] : [9, 9],
          }),
        );
      });
    }, [selectedPropertyId, properties]);

    useEffect(() => {
      if (!targetBounds || !mapRef.current || !L) return;
      try {
        const bounds = L.latLngBounds(
          [targetBounds.south, targetBounds.west],
          [targetBounds.north, targetBounds.east],
        );
        mapRef.current.fitBounds(bounds.pad(0.05));
        didFitBoundsRef.current = true;
      } catch (error) {
        console.warn("MapView.fit target bounds failed", error);
      }
    }, [targetBounds]);

    useEffect(() => {
      if (!mapRef.current) return;
      const map = mapRef.current as any;

      if (modalOpen) {
        if (!interactionStateRef.current) {
          interactionStateRef.current = {
            dragging: map.dragging?.enabled?.() ?? true,
            touchZoom: map.touchZoom?.enabled?.() ?? true,
            scrollWheelZoom: map.scrollWheelZoom?.enabled?.() ?? true,
            doubleClickZoom: map.doubleClickZoom?.enabled?.() ?? true,
            boxZoom: map.boxZoom?.enabled?.() ?? true,
            keyboard: map.keyboard?.enabled?.() ?? true,
            tap: map.tap?.enabled?.(),
          };
        }

        map.dragging?.disable?.();
        map.touchZoom?.disable?.();
        map.scrollWheelZoom?.disable?.();
        map.doubleClickZoom?.disable?.();
        map.boxZoom?.disable?.();
        map.keyboard?.disable?.();
        map.tap?.disable?.();
      } else if (interactionStateRef.current) {
        const state = interactionStateRef.current;
        if (state.dragging) map.dragging?.enable?.();
        if (state.touchZoom) map.touchZoom?.enable?.();
        if (state.scrollWheelZoom) map.scrollWheelZoom?.enable?.();
        if (state.doubleClickZoom) map.doubleClickZoom?.enable?.();
        if (state.boxZoom) map.boxZoom?.enable?.();
        if (state.keyboard) map.keyboard?.enable?.();
        if (state.tap) map.tap?.enable?.();
        interactionStateRef.current = null;
      }
    }, [modalOpen]);

    return (
      <div className={`relative ${className}`}>
        <div
          ref={mapContainerRef}
          style={{ height, width: "100%" }}
          className="rounded-t-3xl overflow-hidden shadow-xl"
        />

        <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm px-3 py-1 rounded-full shadow-md text-xs font-medium text-gray-700 z-[1000]">
          {validProperties.length.toLocaleString()} listings
        </div>

        <style
          dangerouslySetInnerHTML={{
            __html: `
              .mobile-map-marker {
                transition: transform 0.18s ease;
              }
              .mobile-map-marker.selected {
                animation: markerBounce 0.4s ease;
              }
              .marker-flash {
                animation: markerFlash 0.8s ease;
              }
              @keyframes markerBounce {
                0% { transform: translateY(-8px) scale(0.92); opacity: 0.85; }
                100% { transform: translateY(0) scale(1); opacity: 1; }
              }
              @keyframes markerFlash {
                0% { box-shadow: 0 0 0 0 rgba(96,165,250,0.6); }
                100% { box-shadow: 0 0 0 12px rgba(96,165,250,0); }
              }
              .leaflet-control-attribution {
                font-size: 10px;
                background: rgba(15,23,42,0.75);
                color: #e2e8f0;
                padding: 2px 6px;
                border-radius: 6px;
              }
            `,
          }}
        />
      </div>
    );
  },
);

MapView.displayName = "MapView";

export default MapView;

